/** The five print template definitions as pure data, so one shared markup swaps only the CSS. */

// The shared skeleton every template builds on: sheet size, columns, unbreakable sections, dotted leaders.
const BASE = `
  .mdf-sheet { width: 100%; max-width: 210mm; margin: 0 auto; padding: 14mm 12mm; box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .mdf-body { column-count: 2; column-gap: 10mm; }
  .mdf-section { break-inside: avoid; page-break-inside: avoid; margin-bottom: 7mm; }
  .mdf-item { margin-bottom: 3.2mm; }
  .mdf-item-row { display: flex; align-items: baseline; gap: 6px; }
  .mdf-item-name { font-weight: 600; }
  .mdf-leader { flex: 1; border-bottom: 1px dotted currentColor; opacity: .45; transform: translateY(-3px); }
  .mdf-price { font-weight: 600; white-space: nowrap; }
  .mdf-desc { font-size: 10px; line-height: 1.45; opacity: .8; margin-top: 1px; }
  .mdf-variants { margin: 1mm 0 0 4mm; }
  .mdf-variant { display: flex; align-items: baseline; gap: 6px; font-size: 11px; }
  .mdf-badge { font-size: 9px; letter-spacing: .04em; }
  .mdf-veg { color: #1e9e4a; } .mdf-spicy { color: #d43c2f; }
  .mdf-header { text-align: center; margin-bottom: 8mm; }
  .mdf-resto { letter-spacing: .28em; text-transform: uppercase; }
  .mdf-menuname { margin-top: 1mm; }
  .mdf-tagline { font-size: 10px; letter-spacing: .18em; text-transform: uppercase; opacity: .7; margin-top: 2mm; }
  .mdf-footer { text-align: center; font-size: 10px; letter-spacing: .14em; text-transform: uppercase; opacity: .7; margin-top: 8mm; }
  .mdf-sec-title { break-after: avoid; page-break-after: avoid; }
`;

export const MENU_DESIGN_TEMPLATES = [
  {
    id: 'velvet',
    name: 'Velvet Noir',
    blurb: 'Black & gold fine-dining elegance — serif type, gilded rules.',
    swatch: ['#0d0b08', '#c9a24b', '#f2e8d5'],
    css: BASE + `
      .mdf-velvet { background: #0d0b08; color: #e9dfc8; font-family: Georgia, 'Times New Roman', serif; }
      .mdf-velvet .mdf-resto { color: #c9a24b; font-size: 26px; font-weight: 400; }
      .mdf-velvet .mdf-menuname { color: #f2e8d5; font-size: 13px; letter-spacing: .3em; text-transform: uppercase; }
      .mdf-velvet .mdf-header { border-bottom: 1px solid #c9a24b55; padding-bottom: 6mm; }
      .mdf-velvet .mdf-header::before { content: '❧'; display: block; color: #c9a24b; font-size: 18px; margin-bottom: 2mm; }
      .mdf-velvet .mdf-sec-title { color: #c9a24b; font-size: 15px; letter-spacing: .22em; text-transform: uppercase; font-weight: 400; margin: 0 0 3mm; }
      .mdf-velvet .mdf-sec-title::after { content: ''; display: block; width: 12mm; border-bottom: 1px solid #c9a24b; margin-top: 1.5mm; }
      .mdf-velvet .mdf-item-name { color: #f2e8d5; font-size: 12px; letter-spacing: .06em; text-transform: uppercase; font-weight: 400; }
      .mdf-velvet .mdf-price { color: #c9a24b; }
      .mdf-velvet .mdf-desc { color: #b6ab92; font-style: italic; }
      .mdf-velvet .mdf-footer { color: #c9a24b; border-top: 1px solid #c9a24b55; padding-top: 4mm; }
    `,
  },
  {
    id: 'spice',
    name: 'Spice Bazaar',
    blurb: 'Warm terracotta red with cream lettering — festive and hearty.',
    swatch: ['#a8382c', '#f7e3c3', '#7fb0c9'],
    css: BASE + `
      .mdf-spice { background: #a8382c; color: #f7e3c3; font-family: 'Trebuchet MS', Verdana, sans-serif; }
      .mdf-spice .mdf-resto { color: #f7e3c3; font-size: 15px; }
      .mdf-spice .mdf-menuname { color: #cfe3ec; font-size: 30px; font-weight: 700; letter-spacing: .04em; }
      .mdf-spice .mdf-header::after { content: '✦ ✦ ✦'; display: block; color: #f7e3c3aa; font-size: 10px; letter-spacing: .5em; margin-top: 2.5mm; }
      .mdf-spice .mdf-section { background: #963126; border-radius: 4mm; padding: 4mm 5mm; }
      .mdf-spice .mdf-sec-title { color: #cfe3ec; font-size: 14px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; margin: 0 0 2.5mm; }
      .mdf-spice .mdf-item-name { color: #fdf3df; font-size: 11.5px; }
      .mdf-spice .mdf-price { color: #ffd9a1; }
      .mdf-spice .mdf-desc { color: #e9c9a8; }
      .mdf-spice .mdf-leader { border-bottom-style: solid; border-color: #f7e3c3; opacity: .2; }
      .mdf-spice .mdf-footer { color: #f7e3c3; }
    `,
  },
  {
    id: 'sketch',
    name: 'Sketchbook',
    blurb: 'Hand-drawn ink-on-paper look — playful marker headings and doodle boxes.',
    swatch: ['#ffffff', '#1c1c1c', '#4a86b5'],
    css: BASE + `
      .mdf-sketch { background: #fff; color: #1c1c1c; font-family: 'Segoe Print', 'Comic Sans MS', cursive; }
      .mdf-sketch .mdf-resto { font-size: 13px; }
      .mdf-sketch .mdf-menuname { font-size: 34px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; }
      .mdf-sketch .mdf-menuname { text-decoration: underline; text-decoration-style: wavy; text-underline-offset: 6px; text-decoration-thickness: 2px; }
      .mdf-sketch .mdf-section { border: 2px solid #1c1c1c; border-radius: 8px 14px 8px 14px / 14px 8px 14px 8px; padding: 4mm 5mm; }
      .mdf-sketch .mdf-sec-title { font-size: 15px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; margin: 0 0 2.5mm; border-bottom: 2px dashed #1c1c1c; padding-bottom: 1.5mm; }
      .mdf-sketch .mdf-item-name { font-size: 12px; }
      .mdf-sketch .mdf-price { background: #4a86b51a; border: 1.5px solid #1c1c1c; border-radius: 999px; padding: 0 7px; font-size: 11px; }
      .mdf-sketch .mdf-desc { font-size: 9.5px; }
      .mdf-sketch .mdf-leader { border-bottom: none; }
      .mdf-sketch .mdf-footer { font-size: 11px; text-transform: none; letter-spacing: .05em; }
    `,
  },
  {
    id: 'eatery',
    name: 'Daily Press',
    blurb: 'Clean cream café minimal — crisp sans caps, boxed sections, lots of air.',
    swatch: ['#f4efe6', '#211d18', '#8a8375'],
    css: BASE + `
      .mdf-eatery { background: #f4efe6; color: #211d18; font-family: 'Segoe UI', Helvetica, Arial, sans-serif; }
      .mdf-eatery .mdf-resto { font-size: 12px; font-weight: 700; }
      .mdf-eatery .mdf-menuname { font-size: 40px; font-weight: 800; letter-spacing: .3em; text-transform: uppercase; }
      .mdf-eatery .mdf-tagline { color: #8a8375; }
      .mdf-eatery .mdf-header { border-bottom: 2px solid #211d18; padding-bottom: 5mm; }
      .mdf-eatery .mdf-section { border: 1px solid #211d18; padding: 4mm 5mm; background: #faf7f0; }
      .mdf-eatery .mdf-sec-title { font-size: 12px; font-weight: 800; letter-spacing: .2em; text-transform: uppercase; margin: 0 0 3mm; display: inline-block; border-bottom: 2px solid #211d18; padding-bottom: 1mm; }
      .mdf-eatery .mdf-item-name { font-size: 11px; font-weight: 700; }
      .mdf-eatery .mdf-price { font-size: 11px; }
      .mdf-eatery .mdf-desc { color: #6f695d; }
      .mdf-eatery .mdf-footer { border-top: 2px solid #211d18; padding-top: 3mm; }
    `,
  },
  {
    id: 'classico',
    name: 'Classico',
    blurb: 'Warm editorial classic — ivory panel, serif headings, dotted price leaders.',
    swatch: ['#fbf6ee', '#5c4632', '#b98a4e'],
    css: BASE + `
      .mdf-classico { background: #fbf6ee; color: #4a3a2a; font-family: Georgia, 'Times New Roman', serif; }
      .mdf-classico .mdf-sheet { border: 1px solid #b98a4e66; outline: 4px double #b98a4e33; outline-offset: -4mm; }
      .mdf-classico .mdf-resto { color: #5c4632; font-size: 24px; letter-spacing: .18em; }
      .mdf-classico .mdf-menuname { color: #b98a4e; font-size: 13px; letter-spacing: .3em; text-transform: uppercase; font-style: italic; }
      .mdf-classico .mdf-header::after { content: ''; display: block; width: 30mm; margin: 4mm auto 0; border-bottom: 1px solid #b98a4e; }
      .mdf-classico .mdf-sec-title { color: #b98a4e; font-size: 15px; letter-spacing: .18em; text-transform: uppercase; font-weight: 400; margin: 0 0 3mm; text-align: center; }
      .mdf-classico .mdf-item-name { color: #4a3a2a; font-size: 12px; }
      .mdf-classico .mdf-price { color: #5c4632; }
      .mdf-classico .mdf-desc { color: #8a7a64; font-style: italic; }
      .mdf-classico .mdf-leader { border-color: #b98a4e; }
      .mdf-classico .mdf-footer { color: #b98a4e; }
    `,
  },
];

export const templateById = (id) =>
  MENU_DESIGN_TEMPLATES.find((t) => t.id === id) || MENU_DESIGN_TEMPLATES[0];
